import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, ArrowRight, CheckCircle, Search, Copy } from "lucide-react";
import { toast } from "sonner";

export function DeliveryConfirmationDialog({
  open,
  onOpenChange,
  orderData,
  pickupAddress,
  dropAddress,
  onBookAnother
}) {
  const navigate = useNavigate();

  const copyTrackingLink = async () => {
    const url = `${window.location.origin}/track/${orderData?.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied!");
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delivery Booked!</DialogTitle>
        </DialogHeader>
        <div className="text-center py-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          
          {orderData && (
            <>
              <p className="text-2xl font-black mb-2" data-testid="order-id">
                Order #{orderData.id?.slice(-8)}
              </p>
              <p className="text-lg font-bold text-accent mb-4">
                ₹{orderData.price}
              </p>
            </>
          )}

          <div className="bg-secondary/30 border border-border p-4 text-left mb-6 space-y-2">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-green-500" />
              <span className="text-sm truncate">{pickupAddress}</span>
            </div>
            <div className="flex items-center gap-2">
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-red-500" />
              <span className="text-sm truncate">{dropAddress}</span>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-6">
            <Search className="w-4 h-4 animate-pulse" />
            <span>Finding nearest delivery partner...</span>
          </div>

          {/* Share Tracking Link */}
          {orderData?.id && (
            <div className="bg-accent/5 border border-accent/20 p-3 mb-4">
              <p className="text-xs text-muted-foreground mb-2">Share tracking link with recipient:</p>
              <div className="flex gap-2">
                <Input
                  value={`${window.location.origin}/track/${orderData.id}`}
                  readOnly
                  className="rounded-none text-xs h-8"
                  onClick={(e) => e.target.select()}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-none h-8 px-3"
                  onClick={copyTrackingLink}
                  data-testid="copy-tracking-link-btn"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button 
              variant="outline"
              onClick={() => navigate(`/track/${orderData?.id}`)}
              className="flex-1 rounded-none"
              data-testid="track-order-btn"
            >
              Track Order
            </Button>
            <Button 
              onClick={onBookAnother}
              className="flex-1 rounded-none bg-accent text-accent-foreground"
              data-testid="book-another-btn"
            >
              Book Another
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
